import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { PredefinedProfession } from '@/modules/professional/enums/PredefinedProfession';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { Company } from '@/modules/company/models/Company';
import { Profession } from '@/modules/professional/models/Profession';
import { AuthUser } from '@/modules/auth/models/AuthUser';

export class Professional extends BaseEntityModel {
  userId: number;
  companyId?: number;
  professionId: number;
  externalId?: string;

  profession: Relationship<Profession> | null;
  company: Relationship<Company> | null;

  constructor(model: Partial<Professional> = {}) {
    super(model);

    this.userId = model.userId as number;
    this.companyId = model.companyId;
    this.professionId = model.professionId as number;
    this.externalId = model.externalId;

    this.profession = Profession.mapModelOrNull(model.profession);
    this.company = Company.mapModelOrNull(model.company);
  }

  getModelName(): ModelType {
    return ModelType.PROFESSIONAL;
  }

  get isAccountant(): boolean {
    return this.professionId === PredefinedProfession.ACCOUNTANT.id;
  }

  get isFuneralDirector(): boolean {
    return this.professionId === PredefinedProfession.FUNERAL_DIRECTOR.id;
  }

  get isInsuranceBroker(): boolean {
    return this.professionId === PredefinedProfession.INSURANCE_BROKER.id;
  }

  get isLawyer(): boolean {
    return this.professionId === PredefinedProfession.LAWYER.id;
  }

  get isNotary(): boolean {
    return this.professionId === PredefinedProfession.NOTARY.id;
  }

  get isRealEstateAgent(): boolean {
    return this.professionId === PredefinedProfession.REAL_ESTATE_AGENT.id;
  }

  get isBankAdmin(): boolean {
    return this.professionId === PredefinedProfession.BANK_ADMIN.id;
  }

  get isBankCaseManager(): boolean {
    return this.professionId === PredefinedProfession.BANK_CASE_MANAGER.id;
  }

  get isCustomParalegal(): boolean {
    return this.professionId === PredefinedProfession.CUSTOM_PARALEGAL.id;
  }

  get isFinancialAdvisor(): boolean {
    return this.professionId === PredefinedProfession.FINANCIAL_ADVISOR.id;
  }

  get isAssociateFinancialAdvisor(): boolean {
    return this.professionId === PredefinedProfession.ASSOCIATE_FINANCIAL_ADVISOR.id;
  }

  get isFinancialAdvisorAssistant(): boolean {
    return this.professionId === PredefinedProfession.FINANCIAL_ADVISOR_ASSISTANT.id;
  }

  get isInFinancialAdvisorProfessionGroup(): boolean {
    return (
      this.isFinancialAdvisor ||
      this.isAssociateFinancialAdvisor ||
      this.isFinancialAdvisorAssistant
    );
  }

  get isTrustOfficer(): boolean {
    return this.professionId === PredefinedProfession.TRUST_OFFICER.id;
  }

  get isTrustManager(): boolean {
    return this.professionId === PredefinedProfession.TRUST_MANAGER.id;
  }

  get isCaseManagerForPrivateFirm(): boolean {
    return this.professionId === PredefinedProfession.GUARDIAN.id;
  }

  get isEstateProfessional(): boolean {
    return this.professionId === PredefinedProfession.ESTATE_PROFESSIONAL.id;
  }

  get isJuniorEstateProfessional(): boolean {
    return this.professionId === PredefinedProfession.JUNIOR_ESTATE_PROFESSIONAL.id;
  }

  get isEstateManager(): boolean {
    return [
      PredefinedProfession.ESTATE_PROFESSIONAL.id,
      PredefinedProfession.JUNIOR_ESTATE_PROFESSIONAL.id,
      PredefinedProfession.ESTATE_COORDINATOR.id
    ].includes(this.professionId);
  }

  get canBeSetAsPrimary(): boolean {
    return [
      PredefinedProfession.NOTARY.id,
      PredefinedProfession.LAWYER.id,
      PredefinedProfession.ESTATE_PROFESSIONAL.id,
      PredefinedProfession.ESTATE_COORDINATOR.id
    ].includes(this.professionId);
  }

  get canHaveDocumentSharedWith(): boolean {
    return !this.isInFinancialAdvisorProfessionGroup;
  }

  get professionValue(): string | undefined {
    return PredefinedProfession.getByKey(this.professionId, 'id')?.value;
  }

  get isFromB2BCompany(): boolean | undefined {
    return this.company?.isB2BPartner;
  }

  get isPartner(): boolean | undefined {
    return this.company?.isPartner;
  }

  get isConsultant(): boolean | undefined {
    return !!this.company?.isPartner && !this.company?.isClearEstate;
  }

  canBeAssignedToEstate(authUser: AuthUser): boolean {
    const assignableProfessions: boolean = [
      PredefinedProfession.ESTATE_PROFESSIONAL.id,
      PredefinedProfession.JUNIOR_ESTATE_PROFESSIONAL.id,
      PredefinedProfession.ACCOUNTANT.id,
      PredefinedProfession.LAWYER.id,
      PredefinedProfession.NOTARY.id,
      PredefinedProfession.TRUST_OFFICER.id,
      PredefinedProfession.CUSTOM_PARALEGAL.id,
      PredefinedProfession.TRUST_MANAGER.id
    ].includes(this.professionId);

    return authUser.isSuperAdmin
      ? assignableProfessions || this.isInFinancialAdvisorProfessionGroup
      : assignableProfessions;
  }
}
